import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {  useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiClient as axios } from '../utils/apiClient';

import BasicSelector from '../components/ui/BasicSelector.jsx'
import TruncatedText from '../components/ui/TruncatedText.jsx'
import { useSelector,useDispatch } from 'react-redux'
import { setLang } from '../store/langSlice.js'
import {useUser} from '../hooks/'

import {VscAccount} from 'react-icons/vsc'
import {MdNotificationsNone} from 'react-icons/md'
import { MdLanguage, MdLockOutline, MdLogout, MdPersonOutline } from 'react-icons/md'

const safeParse = (value) => {
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
};

const UserBar = ({tenantUsername}) => {
    const [,i18n] = useTranslation('global')
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {getUser} = useUser();
    const user = useSelector((state) => state.auth).user;
    const currentCompany = useSelector((state) => state.currentCompany);
    const currentBranch = useSelector((state) => state.currentBranch);
    const langSelected = useSelector((state)=>state.lang)
    const currentLangValue = String(langSelected?.value || i18n.language || 'ar').toLowerCase().startsWith('ar') ? 'ar' : 'en';
    const userDisplayName = getUser?.name || getUser?.fullName || getUser?.username || getUser?.email || user?.name || user?.fullName || user?.username || user?.email || '';

    const logoutHandler = () => {
        navigate(`/${tenantUsername}/logout`)
    }
    const persistLanguagePreference = (langCode) => {
        const normalizedLang = String(langCode || '').toLowerCase().startsWith('ar') ? 'ar' : 'en';
        try {
            localStorage.setItem('preferredLang', normalizedLang)
            localStorage.setItem('appLang', normalizedLang)
            localStorage.setItem('i18nextLng', normalizedLang)
        } catch {
        }
        if (typeof document !== 'undefined') {
            document.documentElement.lang = normalizedLang;
            document.documentElement.dir = normalizedLang === 'ar' ? 'rtl' : 'ltr';
        }
        return normalizedLang;
    };

    useEffect(() => {
        persistLanguagePreference(currentLangValue);
    }, [currentLangValue]);

    const toggleLangHandler = () => {
        const nextLang = currentLangValue === 'ar' ? 'en' : 'ar';
        const normalizedLang = persistLanguagePreference(nextLang);
        i18n.changeLanguage(normalizedLang)
        dispatch(setLang({
            label: normalizedLang === 'ar' ? 'عربي' : 'English',
            value: normalizedLang
        }))
        saveCentralSelection(
            currentCompany?.value || user?.company_id,
            currentBranch?.value || user?.branch_id,
            normalizedLang
        );
    };


    const onSelectedUserList = (item) => {
        switch (item.value) {
            case 'MyAccount':
                navigate(`/${tenantUsername}/settings`, { state: { tab: 'profile' } })
                break;
            case 'ChangePassword':
                navigate(`/${tenantUsername}/settings`, { state: { tab: 'password' } })
                break;
            case 'Logout':
                logoutHandler()
                break;
            default:
                break;
        }
    }

    const [openListUser,setOpenListUser] = useState(false);
    const userList = useMemo(() => ([
        {
            label: currentLangValue === 'ar' ? 'حسابي' : 'My Account',
            value: 'MyAccount',
            icon: <MdPersonOutline size={16} />
        },
        {
            label: currentLangValue === 'ar' ? 'تغيير كلمة المرور' : 'Change Password',
            value: 'ChangePassword',
            icon: <MdLockOutline size={16} />
        },
        {
            label: currentLangValue === 'ar' ? 'تسجيل الخروج' : 'Logout',
            value: 'Logout',
            icon: <MdLogout size={16} />
        },
    ]), [currentLangValue]);

    const notificationsRef = useRef(null);
    const notificationsButtonRef = useRef(null);
    const notificationsDropdownRef = useRef(null);
    const [notificationsOpen, setNotificationsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [notificationsLoading, setNotificationsLoading] = useState(false);
    const [notificationsLimit, setNotificationsLimit] = useState(10);
    const [lastNotificationsFetchAt, setLastNotificationsFetchAt] = useState(0);
    const [dropdownStyle, setDropdownStyle] = useState({});
    const lastSeenKey = `support_notifications_last_seen_at:${tenantUsername || 'default'}`;
    const [lastSeenAt, setLastSeenAt] = useState(() => {
        try {
            return Number(localStorage.getItem(lastSeenKey) || 0);
        } catch {
            return 0;
        }
    });

    useEffect(() => {
        try {
            setLastSeenAt(Number(localStorage.getItem(lastSeenKey) || 0));
        } catch {
            setLastSeenAt(0);
        }
    }, [lastSeenKey]);

    const getLastActivityMs = (ticket) => {
        const candidates = [];
        [ticket?.lastActivityAt, ticket?.modifiedAt, ticket?.updatedAt, ticket?.createdAt].forEach((raw) => {
            if (!raw) return;
            const ts = new Date(raw).getTime();
            if (Number.isFinite(ts)) candidates.push(ts);
        });
        if (Array.isArray(ticket?.details) && ticket.details.length > 0) {
            const lastDetail = ticket.details[ticket.details.length - 1];
            const ts = lastDetail?.createdAt ? new Date(lastDetail.createdAt).getTime() : NaN;
            if (Number.isFinite(ts)) candidates.push(ts);
        }
        return candidates.length > 0 ? Math.max(...candidates) : 0;
    };

    const unreadCount = useMemo(() => {
        if (!Array.isArray(notifications) || notifications.length === 0) return 0;
        return notifications.filter((t) => {
            const ts = getLastActivityMs(t);
            return ts > lastSeenAt;
        }).length;
    }, [lastSeenAt, notifications]);

    const resolveAuthToken = () => {
        const storedUser = safeParse(localStorage.getItem('user')) || {};
        return (
            localStorage.getItem('token') ||
            sessionStorage.getItem('token') ||
            storedUser?.token ||
            storedUser?.accessToken ||
            storedUser?.access_token ||
            null
        );
    };

    const fetchNotifications = async (limitOverride) => {
        const token = resolveAuthToken();
        if (!token) return;
        const limit = Number.isFinite(Number(limitOverride)) ? Number(limitOverride) : notificationsLimit;
        setNotificationsLoading(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const baseUrl = process.env.REACT_APP_SUPPORT_API_BASE_URL;

            const primary = await axios.get(
                `${baseUrl}/notifications?limit=${encodeURIComponent(limit)}`,
                { headers, validateStatus: () => true }
            );
            const data = Array.isArray(primary?.data?.data) ? primary.data.data : [];

            const normalized = (data || []).map((t) => ({
                ...t,
                lastActivityAt: (() => {
                    const ts = getLastActivityMs(t);
                    return ts ? new Date(ts).toISOString() : null;
                })()
            }));

            const uniq = new Map();
            normalized.forEach((item) => {
                const id = String(item?._id || '');
                if (!id) return;
                uniq.set(id, item);
            });
            setNotifications(Array.from(uniq.values()));
            setLastNotificationsFetchAt(Date.now());
        } finally {
            setNotificationsLoading(false);
        }
    };

    useEffect(() => {
        if (!user?._id && !user?.id && !user?.userId) return;
        fetchNotifications();
        const id = setInterval(() => {
            fetchNotifications();
        }, 30000);
        return () => clearInterval(id);
    }, [user?._id, user?.id, user?.userId]);

    useEffect(() => {
        const handler = (event) => {
            const deletedId = event?.detail?.id ? String(event.detail.id) : '';
            if (!deletedId) return;
            setNotifications((prev) => (Array.isArray(prev) ? prev.filter((t) => String(t?._id || '') !== deletedId) : prev));
        };
        window.addEventListener('support-ticket-deleted', handler);
        return () => window.removeEventListener('support-ticket-deleted', handler);
    }, []);

    useEffect(() => {
        if (!notificationsOpen) return;
        const handleClickOutside = (event) => {
            if (notificationsButtonRef.current?.contains(event.target)) return;
            if (notificationsDropdownRef.current?.contains(event.target)) return;
            setNotificationsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [notificationsOpen]);
    const saveCentralSelection = async (selectedCompanyId, selectedBranchId, selectedLang) => {
        if (!user?.tenant_id) return;
        const token = localStorage.getItem('token');
        if (!token) return;
        try {
            await axios.put(
                `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central`,
                {
                    tenantId: user.tenant_id,
                    scope: 'header-selection',
                    data: {
                        selectedCompanyId,
                        selectedBranchId,
                        selectedLang
                    }
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                    validateStatus: () => true
                }
            );
        } catch (error) {
        }
    };
    return (
        <div className='flex items-center justify-between md:justify-end gap-2 text-xs text-white w-full md:w-auto'>

            <BasicSelector 
                border={false}
                listItem={userList}
                label={userDisplayName}
                defaultValue={userDisplayName}
                openList={openListUser}
                setOpenList={setOpenListUser}
                withChangeLabel={false}
                onSelected={onSelectedUserList}
                >
                    <VscAccount size={14}/> 
                </BasicSelector>

            <div className='text-white/70'>|</div>

            <div ref={notificationsRef} className="relative flex items-center">
                <button
                    ref={notificationsButtonRef}
                    type="button"
                    onClick={() => {
                        const next = !notificationsOpen;
                        setNotificationsOpen(next);
                        if (next) {
                            setNotificationsLimit(10);
                            const rect = notificationsButtonRef.current?.getBoundingClientRect?.();
                            const isRtl = currentLangValue === 'ar';
                            if (rect) {
                                const dropdownEstimatedHeight = 420;
                                const desiredTop = rect.bottom + 8;
                                const maxTop = Math.max(8, window.innerHeight - 8 - dropdownEstimatedHeight);
                                const top = Math.min(desiredTop, maxTop);
                                const right = Math.max(8, window.innerWidth - rect.right);
                                const left = Math.max(8, rect.left);
                                setDropdownStyle({
                                    position: 'fixed',
                                    top,
                                    ...(isRtl ? { left } : { right }),
                                    width: 340,
                                    maxWidth: '90vw',
                                    zIndex: 2147483647
                                });
                            }
                            const now = Date.now();
                            setLastSeenAt(now);
                            try {
                                localStorage.setItem(lastSeenKey, String(now));
                            } catch {
                            }
                            if (Date.now() - lastNotificationsFetchAt > 5000) {
                                fetchNotifications(10);
                            }
                        }
                    }}
                    className="cursor-pointer text-xs flex items-center justify-between whitespace-nowrap transition-all hover:text-indigo-500 gap-2"
                    style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit' }}
                    aria-label="Notifications"
                >
                    <span style={{ position: 'relative', display: 'inline-flex' }}>
                        <MdNotificationsNone size={16} />
                        {unreadCount > 0 && (
                            <span style={{
                                position: 'absolute',
                                top: -6,
                                right: -6,
                                minWidth: 16,
                                height: 16,
                                padding: '0 4px',
                                borderRadius: 999,
                                background: '#ef4444',
                                color: '#fff',
                                fontSize: 10,
                                fontWeight: 800,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                lineHeight: 1
                            }}>
                                {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                        )}
                    </span>
                </button>
            </div>

            {notificationsOpen && createPortal(
                <div
                    ref={notificationsDropdownRef}
                    className="bg-white shadow-lg border rounded-md"
                    style={dropdownStyle}
                >
                    <div style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#0f172a' }}>
                            {currentLangValue === 'ar' ? 'الإشعارات' : 'Notifications'}
                        </div>
                        <button
                            type="button"
                            onClick={() => fetchNotifications(notificationsLimit)}
                            style={{ background: 'transparent', border: 'none', padding: 0, fontSize: 12, fontWeight: 800, color: '#6366f1' }}
                        >
                            {currentLangValue === 'ar' ? 'تحديث' : 'Refresh'}
                        </button>
                    </div>

                    <div style={{ maxHeight: 360, overflow: 'auto' }}>
                        {notificationsLoading ? (
                            <div style={{ padding: 16, color: '#64748b', fontSize: 12 }}>
                                {currentLangValue === 'ar' ? 'جاري التحميل...' : 'Loading...'}
                            </div>
                        ) : Array.isArray(notifications) && notifications.length > 0 ? (
                            notifications.map((t) => {
                                const ts = getLastActivityMs(t);
                                const d = ts ? new Date(ts) : null;
                                const when = d && !Number.isNaN(d.getTime())
                                    ? d.toLocaleString(currentLangValue === 'ar' ? 'ar-EG' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })
                                    : '-';
                                const preview = String(t?.previewText || '').trim();
                                return (
                                    <button
                                        key={t?._id || `${t?.ticketNumber || ''}:${t?.subject || ''}`}
                                        type="button"
                                        onClick={() => {
                                            setNotificationsOpen(false);
                                            if (t?._id) {
                                                navigate(`/${tenantUsername}/tickets/show/${t._id}`);
                                            }
                                        }}
                                        style={{
                                            width: '100%',
                                            textAlign: 'start',
                                            padding: '12px 12px',
                                            border: 'none',
                                            background: 'transparent',
                                            cursor: 'pointer',
                                            borderBottom: '1px solid #f1f5f9'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                                            <TruncatedText
                                                text={`#${t?.ticketNumber || '-'} · ${t?.subject || '-'}`}
                                                style={{ fontSize: 12, fontWeight: 900, color: '#0f172a' }}
                                            />
                                        </div>
                                        {preview ? (
                                            <div style={{ marginTop: 4, fontSize: 11, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {preview}
                                            </div>
                                        ) : null}
                                        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                                            <div style={{ fontSize: 11, color: '#64748b' }}>
                                                {when}
                                            </div>
                                            <div style={{ fontSize: 11, fontWeight: 800, color: '#6366f1' }}>
                                                {(t?.statusId || t?.status) ? String(t?.statusId || t?.status) : ''}
                                            </div>
                                        </div>
                                    </button>
                                );
                            })
                        ) : (
                            <div style={{ padding: 16, color: '#64748b', fontSize: 12 }}>
                                {currentLangValue === 'ar' ? 'لا توجد إشعارات حالياً' : 'No notifications'}
                            </div>
                        )}
                    </div>

                    {Array.isArray(notifications) && notifications.length >= notificationsLimit && (
                        <div style={{ padding: 10, borderTop: '1px solid #e2e8f0' }}>
                            <button
                                type="button"
                                onClick={() => {
                                    const nextLimit = notificationsLimit + 10;
                                    setNotificationsLimit(nextLimit);
                                    fetchNotifications(nextLimit);
                                }}
                                disabled={notificationsLoading}
                                style={{
                                    width: '100%',
                                    background: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: 8,
                                    padding: '10px 12px',
                                    fontSize: 12,
                                    fontWeight: 800,
                                    color: '#0f172a',
                                    cursor: notificationsLoading ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {currentLangValue === 'ar' ? 'عرض المزيد' : 'Load more'}
                            </button>
                        </div>
                    )}
                </div>,
                document.body
            )}
            
            <div className='text-white/70'>|</div>

            <button
                type="button"
                onClick={toggleLangHandler}
                className="cursor-pointer text-xs flex items-center justify-center whitespace-nowrap transition-all hover:text-indigo-200 px-2"
                style={{ background: 'transparent', border: 'none', padding: 0, color: 'inherit' }}
                aria-label="Toggle language"
                title={currentLangValue === 'ar' ? 'English' : 'عربي'}
            >
                <span className="inline-flex h-6 w-6 items-center justify-center text-[18px] leading-none">
                    <MdLanguage size={19} />
                </span>
            </button>
        </div>
    )
}

export default UserBar
